import React, { useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Box, Alert, Button } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as People,
  Forum as ForumIcon,
  StickyNote2 as NoteIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { AuthContext } from '../../../contexts/AuthContext';
import StudentLayout from '../StudentLayout';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import SidebarWithAccordion from '../../../components/Dashboard/SidebarWithAccordion';
import StudentStatsSection from '../../../components/Dashboard/Student/StudentStatsSection';
import StudentQuestionsTab from '../../../components/Dashboard/Student/StudentQuestionsTab';
import TeacherQuestionsTab from '../../../components/Dashboard/Teacher/TeacherQuestionsTab';
import { useTranslation } from 'react-i18next';
import { useQuestionsPageData } from './hooks/useQuestionsPageData';
import { useQuestionsPageHandlers } from './hooks/useQuestionsPageHandlers';
import NotebookCardGrid from './components/NotebookCardGrid';
import StudentQuestionCharts from './components/StudentQuestionCharts';
import ViewQuestionStudentsDialog from './components/ViewQuestionStudentsDialog';

function QuestionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { category, notebookId } = useParams();
  const { selectedCourse, user, logout } = useContext(AuthContext);
  const { t } = useTranslation();
  const isTeacher = user?.role === 'teacher';
  const viewWeak = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('view') === 'weak';
  }, [location.search]);

  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const [questionStudents, setQuestionStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const data = useQuestionsPageData({
    selectedCourse,
    notebookId,
    category,
    viewWeak,
    isTeacher,
  });

  const {
    questions,
    setQuestions,
    categories,
    loading,
    notebooks,
    expandedSections,
    setExpandedSections,
    sortBy,
    setSortBy,
    selectedCategory,
    prefetchQuestions,
    refetchData,
  } = data;

  const handlers = useQuestionsPageHandlers({
    navigate,
    selectedCourse,
    setQuestions,
    refetchData,
    setSelectedQuestion,
    setStudentsDialogOpen,
    setQuestionStudents,
    setLoadingStudents,
  });

  const {
    handleViewQuestionStudents,
    handleDeleteQuestion,
    getStatusIcon,
    handleCategoryChange,
  } = handlers;

  const totalQuestions = categories.reduce((sum, cat) => sum + cat.count, 0);
  const totalCompleted = categories.reduce((sum, cat) => sum + cat.completed, 0);
  const totalAttempted = categories.reduce((sum, cat) => sum + cat.attempted, 0);
  const completionRate =
    totalQuestions > 0 ? Math.round((totalCompleted / totalQuestions) * 100) : 0;

  const notebookCategories = categories.filter((cat) =>
    questions.some((q) => q.category === cat.category && q.isNotebookExample)
  );

  const getAppBarTitle = () => {
    if (notebookId) {
      const notebookQuestion = questions.find(
        (q) => q.isNotebookExample && q.notebookId === notebookId
      );
      return notebookQuestion?.notebookTitle || '笔记本题目';
    }
    return selectedCategory === 'all' ? t('dashboard.student.allQuestions') : selectedCategory;
  };

  const handleSectionToggle = (sectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const onViewQuestions = (notebookIdForCard, notebookTitle) => {
    if (notebookIdForCard) {
      navigate(`/questions/notebook/${notebookIdForCard}`);
    } else {
      navigate(`/questions/category/${encodeURIComponent(notebookTitle)}`);
    }
  };

  // 笔记本页：若当前 questions 不是该笔记本的（仍是上一页的「全部」），不渲染整表，只显示 loading，避免首帧 282 条卡顿
  const isQuestionsForCurrentView =
    !notebookId ||
    questions.length === 0 ||
    questions.every((q) => q.isNotebookExample && q.notebookId === notebookId);
  const displayQuestions = isQuestionsForCurrentView ? questions : [];
  const listLoading = loading || (notebookId && questions.length > 0 && !isQuestionsForCurrentView);

  if (!selectedCourse) {
    return (
      <Container maxWidth="xl" sx={{ bgcolor: 'transparent' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography color="text.secondary">请先选择课程</Typography>
        </Box>
      </Container>
    );
  }

  if (isTeacher) {
    const handleLogout = () => {
      logout();
      navigate('/');
    };
    const handleSettingsClick = () => navigate('/teacher/settings');

    const drawerContent = (
      <SidebarWithAccordion
        courseName={selectedCourse?.name}
        courseTermCode={selectedCourse?.termCode}
        userInfo={{ name: user?.name, role: '教师账号', avatar: user?.avatar }}
        onLogout={handleLogout}
        onSettingsClick={handleSettingsClick}
        navigationItems={[
          {
            icon: <StoreIcon color={location.pathname === '/marketplace' ? 'primary' : 'inherit'} />,
            label: '商城',
            selected: location.pathname === '/marketplace',
            onClick: () => navigate('/marketplace'),
          },
          {
            icon: <DashboardIcon color={location.pathname === '/dashboard' ? 'primary' : 'inherit'} />,
            label: '总览',
            selected: location.pathname === '/dashboard',
            onClick: () => navigate('/dashboard'),
          },
          {
            icon: (
              <People
                color={
                  location.pathname.startsWith('/dashboard') &&
                  !location.pathname.includes('/questions') &&
                  !location.pathname.includes('/forum') &&
                  !location.pathname.includes('/notebooks')
                    ? 'primary'
                    : 'inherit'
                }
              />
            ),
            label: '学生管理',
            selected:
              location.pathname.startsWith('/dashboard') &&
              !location.pathname.includes('/questions') &&
              !location.pathname.includes('/forum') &&
              !location.pathname.includes('/notebooks'),
            onClick: () => navigate('/dashboard'),
          },
          {
            icon: <NoteIcon color={location.pathname.startsWith('/notebooks') ? 'primary' : 'inherit'} />,
            label: '课程笔记',
            selected: location.pathname.startsWith('/notebooks'),
            onClick: () => navigate('/notebooks'),
          },
          {
            icon: <ForumIcon color={location.pathname.startsWith('/forum') ? 'primary' : 'inherit'} />,
            label: '课程论坛',
            selected: location.pathname.startsWith('/forum'),
            onClick: () => navigate('/forum'),
          },
        ]}
      />
    );

    return (
      <>
        <DashboardLayout
          drawerContent={drawerContent}
          appBarTitle={getAppBarTitle()}
          courseName={selectedCourse?.name}
          courseTermCode={selectedCourse?.termCode}
        >
          <Container maxWidth="xl" sx={{ bgcolor: 'transparent' }}>
            {selectedCategory === 'all' && !notebookId && (
              <>
                <StudentStatsSection
                  totalQuestions={totalQuestions}
                  totalAttempted={totalAttempted}
                  totalCompleted={totalCompleted}
                  completionRate={completionRate}
                />
                <NotebookCardGrid
                  notebookCategories={notebookCategories}
                  questions={questions}
                  prefetchQuestions={prefetchQuestions}
                  onViewQuestions={onViewQuestions}
                  isTeacher
                />
              </>
            )}

            {!(selectedCategory === 'all' && !notebookId) && (
              <Box>
                {viewWeak && (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                      <Button size="small" color="inherit" onClick={() => navigate(location.pathname)}>
                        查看全部题目
                      </Button>
                    }
                  >
                    当前显示：易错/未满分题目（有学生未通过或未拿满分的题目）
                  </Alert>
                )}
                {notebookId ? (
                <TeacherQuestionsTab
                  questions={displayQuestions}
                  categories={categories}
                  selectedNotebook={notebookId}
                  notebooks={notebooks}
                  loading={listLoading}
                  expandedSections={expandedSections}
                    onSectionToggle={handleSectionToggle}
                    onViewStudents={handleViewQuestionStudents}
                    onDelete={handleDeleteQuestion}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    selectedCourse={selectedCourse}
                    onQuestionUpdate={(updated) => {
                      setQuestions((prev) =>
                        prev.map((q) =>
                          String(q.id ?? q._id) === String(updated.id ?? updated._id)
                            ? { ...q, ...updated }
                            : q
                        )
                      );
                    }}
                    onQuestionClick={(question, context) => {
                      if (question.isNotebookExample) {
                        navigate(`/notebook-exercise/${question.id.replace('notebook_', '')}`, { state: context ?? null });
                      } else {
                        navigate(`/question/${question._id || question.id}`, { state: context ?? null });
                      }
                    }}
                  />
              ) : (
                <StudentQuestionsTab
                  questions={displayQuestions}
                  loading={listLoading}
                  expandedSections={expandedSections}
                  onSectionToggle={handleSectionToggle}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  getStatusIcon={getStatusIcon}
                  onQuestionClick={(question, context) => {
                    if (question.isNotebookExample) {
                      navigate(`/notebook-exercise/${question.id.replace('notebook_', '')}`, { state: context ?? null });
                    } else {
                      navigate(`/question/${question._id || question.id}`, { state: context ?? null });
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Container>
      </DashboardLayout>

        <ViewQuestionStudentsDialog
          open={studentsDialogOpen}
          onClose={() => setStudentsDialogOpen(false)}
          selectedQuestion={selectedQuestion}
          questionStudents={questionStudents}
          loadingStudents={loadingStudents}
          variant="teacher"
        />
      </>
    );
  }

  return (
    <>
      <StudentLayout
        appBarTitle={getAppBarTitle()}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      >
        <Container maxWidth="xl" sx={{ bgcolor: 'transparent' }}>
          {selectedCategory === 'all' && !notebookId && (
            <>
              <StudentStatsSection
                totalQuestions={totalQuestions}
                totalAttempted={totalAttempted}
                totalCompleted={totalCompleted}
                completionRate={completionRate}
              />
              <StudentQuestionCharts
                selectedCourse={selectedCourse}
                totalQuestions={totalQuestions}
                totalAttempted={totalAttempted}
                totalCompleted={totalCompleted}
              />
            </>
          )}

          {!(selectedCategory === 'all' && !notebookId) && (
            <StudentQuestionsTab
              questions={displayQuestions}
              loading={listLoading}
              expandedSections={expandedSections}
              onSectionToggle={handleSectionToggle}
              sortBy={sortBy}
              onSortChange={setSortBy}
              getStatusIcon={getStatusIcon}
              onQuestionClick={(question, context) => {
                if (question.isNotebookExample) {
                  navigate(`/notebook-exercise/${question.id.replace('notebook_', '')}`, { state: context ?? null });
                } else {
                  navigate(`/question/${question._id || question.id}`, { state: context ?? null });
                }
              }}
            />
          )}
        </Container>
      </StudentLayout>

      <ViewQuestionStudentsDialog
        open={studentsDialogOpen}
        onClose={() => setStudentsDialogOpen(false)}
        selectedQuestion={selectedQuestion}
        questionStudents={questionStudents}
        loadingStudents={loadingStudents}
        variant="student"
      />
    </>
  );
}

export default QuestionsPage;
